import { Button, Form, PageHeader, Trash } from '@lmc/ui-kit'
import { Form as AntdForm } from 'antd'
import { showToast } from 'common/utils'
import { useCanEditServiceUserData } from 'components/AccessControl'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import type { UpdateResidentReturnType } from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  ButtonsWrapper,
  ProfileContent
} from '../ResidentProfileScreen/styledComponents'
import {
  handleOnFinishFailed,
  scrollToTop
} from '../ResidentProfileScreen/utils/utils'

type ImportantContactFormProps<T> = {
  children: ReactNode
  title: string
  updateResident: (values: T) => Promise<UpdateResidentReturnType>
  initialValues: Partial<T> | undefined
  onDelete: (() => void) | undefined
  testId?: string
}

const ImportantContactForm = <T extends Record<string, any>>({
  children,
  title,
  updateResident,
  initialValues,
  onDelete,
  testId
}: ImportantContactFormProps<T>) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const hasRolePermission = useCanEditServiceUserData()

  const [form] = AntdForm.useForm()
  const navigate = useNavigate()
  const topRef = useRef<HTMLDivElement | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const { id: residentID } = useParams()

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  const handleOnFinish = async (values: T) => {
    try {
      setIsLoading(true)
      await updateResident(values)

      showToast({
        title: t(generalConstants.SUCCESS, { ns: NAMESPACE.GENERAL }),
        type: 'success'
      })

      navigate(`/residents/${residentID}/important-people`)
    } catch {
      setIsLoading(false)
    }
  }

  const handleOnCancel = () => {
    navigate(`/residents/${residentID}/important-people`)
  }

  return (
    <div
      ref={topRef}
      data-testid={
        testId ? `${testId}-important-contact-form` : 'important-contact-form'
      }
    >
      <PageHeader
        sticky
        title={title}
        data-testid={
          testId
            ? `${testId}-important-contact-header`
            : 'important-contact-header'
        }
      >
        <ButtonsWrapper
          data-testid={
            testId
              ? `${testId}-important-contact-buttons`
              : 'important-contact-buttons'
          }
        >
          {onDelete && (
            <ConfirmationModal>
              <ButtonStyled
                onClick={onDelete}
                variant="danger"
                disabledByPermission={!hasRolePermission}
                data-testid={
                  testId
                    ? `${testId}-important-contact-delete-btn`
                    : 'important-contact-delete-btn'
                }
              >
                <Trash />
              </ButtonStyled>
            </ConfirmationModal>
          )}
          <Button
            onClick={handleOnCancel}
            data-testid={
              testId
                ? `${testId}-important-contact-cancel-btn`
                : 'important-contact-cancel-btn'
            }
          >
            {hasRolePermission ? t(supConstants.CANCEL) : t(supConstants.BACK)}
          </Button>
          <Button
            variant="primary"
            onClick={form.submit}
            type="submit"
            isLoading={isLoading}
            disabledByPermission={!hasRolePermission}
            data-testid={
              testId
                ? `${testId}-important-contact-save-btn`
                : 'important-contact-save-btn'
            }
          >
            {t(supConstants.SAVE)}
          </Button>
        </ButtonsWrapper>
      </PageHeader>
      <ProfileContent
        data-testid={
          testId
            ? `${testId}-important-contact-content`
            : 'important-contact-content'
        }
      >
        <Form<T>
          disabled={!hasRolePermission}
          onFinishFailed={({ errorFields }) =>
            handleOnFinishFailed(errorFields)
          }
          form={form}
          name="important-contact-form"
          onFinish={handleOnFinish}
          // onValuesChange={handleFormValuesChange}
          initialValues={initialValues}
          data-testid={
            testId
              ? `${testId}-important-contact-form-element`
              : 'important-contact-form-element'
          }
        >
          {children}
        </Form>
      </ProfileContent>
    </div>
  )
}

const ButtonStyled = styled(Button)`
  height: 24px;
`

export default ImportantContactForm
